<!--
  通用树视图组件
  职责：统一 NTree 的高度、外层横向滚动和基础节点样式。
-->
<script setup lang="ts">
import { NTree } from 'naive-ui'
import type { TreeOption } from 'naive-ui'
import type { VNodeChild } from 'vue'

type DropPosition = 'before' | 'inside' | 'after'

type TreeDropCheckInfo = {
  dropPosition: DropPosition
  node: TreeOption
  phase: 'drag' | 'drop'
}

type TreeDragInfo = {
  node: TreeOption
}

type TreeDropInfo = {
  node: TreeOption
  dragNode: TreeOption
  dropPosition: DropPosition
}

/**
 * 统一树控件的显示与滚动配置。
 * @props data 树节点数据。
 * @props expandedKeys 已展开节点 key。
 * @props selectedKeys 已选中节点 key。
 * @props draggable 是否允许拖拽。
 * @props selectable 是否允许选择。
 * @props allowDrop 拖拽落点校验。
 * @props nodeProps 节点 DOM 事件扩展。
 * @props renderLabel 自定义节点文本区域渲染。
 * @emits update:expandedKeys 展开状态变化。
 * @emits update:selectedKeys 选中状态变化。
 * @emits dragstart 开始拖拽节点。
 * @emits dragend 结束拖拽节点。
 * @emits drop 节点完成拖拽落点。
 */
const props = withDefaults(defineProps<{
  data: TreeOption[]
  expandedKeys?: Array<string | number>
  selectedKeys?: Array<string | number>
  draggable?: boolean
  selectable?: boolean
  allowDrop?: (info: TreeDropCheckInfo) => boolean
  nodeProps?: (info: { option: TreeOption }) => Record<string, unknown>
  renderLabel?: (info: { option: TreeOption }) => VNodeChild
}>(), {
  expandedKeys: () => [],
  selectedKeys: () => [],
  draggable: false,
  selectable: true,
  allowDrop: undefined,
  nodeProps: undefined,
  renderLabel: undefined,
})

const emit = defineEmits<{
  'update:expandedKeys': [value: Array<string | number>]
  'update:selectedKeys': [value: Array<string | number>]
  dragstart: [value: TreeDragInfo]
  dragend: []
  drop: [value: TreeDropInfo]
}>()

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <div
    v-bind="$attrs"
    class="app-tree-view"
  >
    <n-tree
      block-line
      expand-on-click
      virtual-scroll
      :animated="false"
      :allow-drop="allowDrop"
      :data="data"
      :draggable="draggable"
      :node-props="nodeProps"
      :render-label="renderLabel"
      :selectable="selectable"
      :expanded-keys="expandedKeys"
      :selected-keys="selectedKeys"
      class="app-tree-view__tree"
      @update:expanded-keys="emit('update:expandedKeys', $event)"
      @update:selected-keys="emit('update:selectedKeys', $event)"
      @dragstart="emit('dragstart', $event)"
      @dragend="emit('dragend')"
      @drop="emit('drop', $event)"
    />
  </div>
</template>

<style scoped>
.app-tree-view {
  flex: 1 1 0;
  width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 var(--app-space-xs) var(--app-space-xs);
  box-sizing: border-box;
  transform: translateZ(0);
}

.app-tree-view__tree {
  width: max-content;
  min-width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-tree-view__tree :deep(.n-scrollbar-container) {
  height: 100%;
  max-height: 100%;
}

.app-tree-view__tree :deep(.n-scrollbar-content),
.app-tree-view__tree :deep(.n-virtual-list) {
  width: max-content;
  min-width: 100%;
}

.app-tree-view__tree :deep(.v-vl),
.app-tree-view__tree :deep(.v-vl-items),
.app-tree-view__tree :deep(.v-vl-visible-items) {
  width: max-content;
  min-width: 100%;
}

.app-tree-view__tree :deep(.n-tree-node-wrapper),
.app-tree-view__tree :deep(.n-tree-node) {
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
}

.app-tree-view__tree :deep(.n-tree-node-content) {
  width: auto;
  max-width: none;
  min-width: max-content;
  min-height: var(--app-list-row-height-sm);
  padding-left: var(--app-space-xs);
  padding-right: var(--app-space-xs);
  margin-bottom: 0;
}

.app-tree-view__tree :deep(.n-tree-node-content:hover) {
  background: var(--theme-bg-hover);
}

.app-tree-view__tree :deep(.n-tree-node-content--selected) {
  background: var(--theme-bg-selected);
}

.app-tree-view__tree :deep(.n-tree-node-switcher) {
  color: var(--theme-text-very-muted);
}

.app-tree-view__tree :deep(.n-tree-node-content__text) {
  display: inline-block;
  width: auto;
  font-size: var(--app-font-size-sm);
  color: var(--theme-text-secondary);
  font-weight: 500;
  white-space: nowrap;
}
</style>
